import type { SearchResult } from '@carboncopyco/data';
import { MoreVerticalOutlineIcon } from '@carboncopyco/icons';
import type {
  DemoOverlay,
  IDemo,
  IRecording,
  Playbook,
  Tour,
  WorkspaceFolder,
} from '@carboncopyco/types';
import { Button, Separator, TooltipBubble } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import type { ReactElement, ReactNode } from 'react';
import type { TableViewType } from '../../types';
import { FolderActions } from './Folders';
import { useWorkspacePageProps } from './WorkspacePageContext';
import type { ExtendedWorkspaceFolder } from './WorkspacePageTableView';
import { CloneActionsDropdown } from './tiles/EntitiesActionsDropdown/CloneActionsDropdown';
import { DemoActionsDropdown } from './tiles/EntitiesActionsDropdown/DemoActionsDropdown';
import { OverlayActionsDropdown } from './tiles/EntitiesActionsDropdown/OverlayActionsDropdown';
import { PlaybookActionsDropdown } from './tiles/EntitiesActionsDropdown/PlaybookActionsDropdown';
import { TourActionsDropdown } from './tiles/EntitiesActionsDropdown/TourActionsDropdown';

interface WorkspacePageTableRowWrapperProps {
  children?: ReactNode;
  item: SearchResult | ExtendedWorkspaceFolder;
}
export const WorkspacePageTableRowWrapper = ({
  item,
  children,
}: WorkspacePageTableRowWrapperProps) => {
  const {
    goToDemo,
    goToClone,
    goToTour,
    goToPlaybook,
    goToFolderPage,
    workspaceId,
    goToArchivedFolderPage,
  } = useWorkspacePageProps();
  const [isHover, setIsHover] = useBoolean();
  const [isActive, { toggle: toggleActive }] = useBoolean();

  const isEntityArchived = item.isDeleted;
  const entityId = Number(item.id);

  const navigateByEntity: Record<TableViewType, () => void> = {
    record: () => (isEntityArchived ? undefined : goToClone(entityId)),
    demo: () =>
      isEntityArchived ? undefined : goToDemo(String(entityId), workspaceId),
    tour: () => (isEntityArchived ? undefined : goToTour(entityId)),
    overlay: () =>
      isEntityArchived ? undefined : goToDemo(String(entityId), workspaceId),
    playbook: () =>
      isEntityArchived ? undefined : goToPlaybook(String(entityId)),
    folder: () =>
      isEntityArchived
        ? goToArchivedFolderPage(String(item.id), workspaceId)
        : goToFolderPage(String(item.id), workspaceId),
  };

  const showDropdownByEntity: Record<TableViewType, ReactElement> = {
    folder: (
      <FolderActions
        isArchived={item.isDeleted}
        folderId={String(item.id)}
        folderName={(item as WorkspaceFolder).name}
        onOpenChange={toggleActive}
        open={isActive}
      />
    ),
    record: (
      <CloneActionsDropdown
        archived={item.isDeleted}
        clone={item as IRecording}
        handleMenuOpen={toggleActive}
        triggerElement={
          <Button
            dark
            xSmall
            icon={<MoreVerticalOutlineIcon />}
          />
        }
      />
    ),
    demo: (
      <DemoActionsDropdown
        archived={item.isDeleted}
        demo={item as IDemo}
        handleMenuOpen={toggleActive}
        triggerElement={
          <Button
            dark
            xSmall
            icon={<MoreVerticalOutlineIcon />}
          />
        }
      />
    ),
    tour: (
      <TourActionsDropdown
        archived={item.isDeleted}
        tour={item as Tour}
        handleMenuOpen={toggleActive}
        triggerElement={
          <Button
            dark
            xSmall
            icon={<MoreVerticalOutlineIcon />}
          />
        }
      />
    ),
    overlay: (
      <OverlayActionsDropdown
        archived={item.isDeleted}
        demo={item as DemoOverlay}
        handleMenuOpen={toggleActive}
        triggerElement={
          <Button
            dark
            xSmall
            icon={<MoreVerticalOutlineIcon />}
          />
        }
      />
    ),
    playbook: (
      <PlaybookActionsDropdown
        playbook={item as Playbook}
        handleMenuOpen={toggleActive}
        archived={item.isDeleted}
        triggerElement={
          <Button
            dark
            xSmall
            icon={<MoreVerticalOutlineIcon />}
          />
        }
      />
    ),
  };

  return (
    <div
      onMouseEnter={setIsHover.setTrue}
      onMouseLeave={setIsHover.setFalse}
      className={classNames('flex flex-col ', {
        'bg-ds-natural-50 cursor-pointer':
          isHover && (!item.isDeleted || item.entityType === 'folder'),
        'bg-none cursor-default': !isHover,
        'bg-ds-natural-50 cursor-default':
          isHover && (item.isDeleted || item.entityType !== 'folder'),
      })}
      onClick={(event) => {
        navigateByEntity[item.entityType](event);
      }}
    >
      <div className="flex items-center justify-between pr-6">
        <div className="py-4">{children}</div>
        {!item?.readOnly ? (
          <div className={isHover || isActive ? 'visible' : 'invisible'}>
            <TooltipBubble
              title="More actions"
              placement="top"
              disabled={item.entityType === 'folder'}
            >
              {showDropdownByEntity[item.entityType]}
            </TooltipBubble>
          </div>
        ) : null}
      </div>
      <Separator horizontal />
    </div>
  );
};
